import type {
  UserRolesPermission,
  WithOptionalInternalProps,
} from './flotiq-client/types/api-response';

type PermissionsTypes = 'CO' | 'CTD';

type PermissionsRule = 'canCreate' | 'canDelete' | 'canRead' | 'canUpdate';

export interface RolePermission extends WithOptionalInternalProps<UserRolesPermission> {
  ctdName?: string | null;
}

interface Permissions {
  CO: Record<string, any>;
  CTD: Record<string, any>;
}

export class RolePermissions {
  userRoles?: RolePermission[];
  permissions?: Permissions;
  visibleCTDs = false;
  visibleCOs = false;

  static PERMISSIONS_TYPES = {
    READ: 'canRead',
    CREATE: 'canCreate',
    DELETE: 'canDelete',
    UPDATE: 'canUpdate',
  };

  constructor(userRoles?: RolePermission[]) {
    this.userRoles = userRoles;

    if (userRoles && userRoles?.length > 0) {
      const currentPermissions: Permissions = { CO: {}, CTD: {} };

      userRoles.forEach((permission) => {
        const type = permission.type;
        const ctdName =
          permission.ctdName || permission.contentTypeDefinition?.name || '*';

        if (!currentPermissions[type][ctdName]) {
          currentPermissions[type][ctdName] = {
            canCreate: permission.canCreate,
            canDelete: permission.canDelete,
            canRead: permission.canRead,
            canUpdate: permission.canUpdate,
          };
        } else {
          ['canCreate', 'canDelete', 'canRead', 'canUpdate'].forEach((can) => {
            if (
              !currentPermissions[type][ctdName][can] &&
              permission[can as PermissionsRule]
            ) {
              currentPermissions[type][ctdName][can] = true;
            }
          });
        }

        ['canCreate', 'canDelete', 'canRead', 'canUpdate'].forEach((can) => {
          const isOverridden = userRoles.find(
            (permission) =>
              permission.type === 'CO' &&
              permission.contentTypeDefinition?.name === ctdName &&
              permission[can as PermissionsRule] &&
              !permission.attributesPermission?.[can as PermissionsRule],
          );

          if (
            !isOverridden &&
            permission.attributesPermission?.[can as PermissionsRule]
          ) {
            (
              permission.attributesPermission[can as PermissionsRule] || []
            ).forEach((field) => {
              if (!currentPermissions[type][ctdName].fields) {
                currentPermissions[type][ctdName].fields = {};
              }
              if (!currentPermissions[type][ctdName].fields[can]) {
                currentPermissions[type][ctdName].fields[can] = {};
              }
              currentPermissions[type][ctdName].fields[can][field] = true;
            });
          }
        });

        if (permission.canRead) {
          if (type === 'CTD') this.visibleCTDs = true;
          else this.visibleCOs = true;
        }
      });

      this.permissions = currentPermissions;
    }
  }

  #checkProperty(
    ctdName = '*',
    permission = RolePermissions.PERMISSIONS_TYPES.READ,
    type = 'CO',
    field?: string,
  ) {
    if (!this.permissions) return true;

    if (
      field &&
      typeof this.permissions[type as PermissionsTypes]?.[ctdName]?.fields?.[
        permission
      ] !== 'undefined'
    ) {
      return !!this.permissions[type as PermissionsTypes]?.[ctdName]?.fields?.[
        permission
      ]?.[field];
    }

    return (
      !!this.permissions[type as PermissionsTypes]?.['*']?.[permission] ||
      !!this.permissions[type as PermissionsTypes]?.[ctdName]?.[permission]
    );
  }

  #checkProperties(ctdName = '*', type = 'CO', field?: string) {
    return {
      canCreate: this.#checkProperty(
        ctdName,
        RolePermissions.PERMISSIONS_TYPES.CREATE,
        type,
        field,
      ),
      canDelete: this.#checkProperty(
        ctdName,
        RolePermissions.PERMISSIONS_TYPES.DELETE,
        type,
        field,
      ),
      canRead: this.#checkProperty(
        ctdName,
        RolePermissions.PERMISSIONS_TYPES.READ,
        type,
        field,
      ),
      canUpdate: this.#checkProperty(
        ctdName,
        RolePermissions.PERMISSIONS_TYPES.UPDATE,
        type,
        field,
      ),
    };
  }

  canCtd(
    ctdName?: string,
    permission = RolePermissions.PERMISSIONS_TYPES.READ,
  ) {
    if (!this.userRoles) return false;

    return this.#checkProperty(ctdName, permission, 'CTD');
  }

  canCo(
    ctdName?: string,
    permission = RolePermissions.PERMISSIONS_TYPES.READ,
    field?: string,
  ) {
    if (!this.userRoles) return false;

    return this.#checkProperty(ctdName, permission, 'CO', field);
  }

  hasCoLimitedFields(ctdName?: string) {
    if (!ctdName || !this.userRoles) return true;
    return typeof this.permissions?.['CO']?.[ctdName]?.fields !== 'undefined';
  }

  getCtdPermissions(ctdName?: string) {
    if (!this.userRoles)
      return {
        canCreate: false,
        canDelete: false,
        canRead: false,
        canUpdate: false,
      };

    return this.#checkProperties(ctdName, 'CTD');
  }

  getCoPermissions(ctdName?: string, field?: string) {
    if (!this.userRoles)
      return {
        canCreate: false,
        canDelete: false,
        canRead: false,
        canUpdate: false,
      };

    return this.#checkProperties(ctdName, 'CO', field);
  }

  ctdsVisible() {
    if (!this.userRoles) return false;

    if (!this.permissions) return true;

    return this.visibleCTDs;
  }

  cosVisible() {
    if (!this.userRoles) return false;

    if (!this.permissions) return true;

    return this.visibleCOs;
  }

  getCtdNamesWithPermission(
    type = 'CO',
    permission = RolePermissions.PERMISSIONS_TYPES.READ,
  ) {
    if (!this.userRoles) return [];

    if (
      !this.permissions ||
      !!this.permissions[type as PermissionsTypes]?.['*']?.[permission]
    )
      return null;

    return Object.entries(this.permissions[type as PermissionsTypes] || {})
      .filter(([, permissions]) => !!permissions[permission])
      .map(([ctdName]) => ctdName);
  }
}
